/* ss-bundle-builder.js — SeatShield Bundle Builder v2
 *
 * Injects "Add to Bundle" buttons onto Impulse product cards, lazy-fetches
 * product data from /products/<handle>.js on first click, builds the variant
 * picker modal in JS, and hydrates/updates the sticky summary panel.
 *
 * Entrypoint: [data-ss-summary] element (ss-bundle-builder-summary.liquid).
 * Exits immediately if that element is absent — safe to load on any page.
 *
 * Spec: docs/specs/ss-bundle-builder.md
 * Adapted from: MudGear mg-bundle-builder.js. Namespace mg → ss, MgCartDrawer → SsCartDrawer.
 */
(function () {
  'use strict';

  var summaryEl = document.querySelector('[data-ss-summary]');
  if (!summaryEl) return;

  // ─── Settings from section data attributes ─────────────────────────────────
  var settings = {
    tier1: { qty: parseInt(summaryEl.dataset.tier1MinQty, 10) || 2,  pct: parseInt(summaryEl.dataset.tier1DiscountPct, 10) || 15 },
    tier2: { qty: parseInt(summaryEl.dataset.tier2MinQty, 10) || 3,  pct: parseInt(summaryEl.dataset.tier2DiscountPct, 10) || 33 },
    tier3: { qty: parseInt(summaryEl.dataset.tier3MinQty, 10) || 5,  pct: parseInt(summaryEl.dataset.tier3DiscountPct, 10) || 40 },
    upsellTemplate: summaryEl.dataset.upsellTemplate || 'Add [n] more item(s) to get [pct]% off!',
    upsellComplete: summaryEl.dataset.upsellComplete || "🎉 You've got the best discount!"
  };

  // Eligible product IDs — seeded server-side, grows as grid updates via observer
  var eligibleProductIds = new Set(
    (window.SsBundleEligibleIds || []).map(String)
  );

  var modalsContainer = null;
  var openModalEl = null;
  var lastFocus = null;
  var modalCache = {};

  // ────────────────────────────────────────────────────────────────────────────
  // PRODUCT ID COLLECTION
  // ────────────────────────────────────────────────────────────────────────────

  function collectProductIds() {
    document.querySelectorAll('#CollectionAjaxContent .grid-product[data-product-id]').forEach(function (el) {
      var id = el.dataset.productId;
      if (id) eligibleProductIds.add(String(id));
    });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // BUTTON INJECTION
  // ────────────────────────────────────────────────────────────────────────────

  function injectButtons() {
    document.querySelectorAll('#CollectionAjaxContent .grid-product[data-product-handle]').forEach(function (card) {
      if (card.querySelector('[data-ss-add-btn]')) return;
      var handle = card.dataset.productHandle;
      if (!handle) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ss-add-to-bundle-btn';
      btn.dataset.ssAddBtn = '';
      btn.dataset.productHandle = handle;
      btn.dataset.productId = card.dataset.productId || '';
      btn.textContent = 'Add to Bundle';
      card.appendChild(btn);
    });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // "ADD TO BUNDLE" CLICK — delegated on document
  // ────────────────────────────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ss-add-btn]');
    if (!btn) return;
    e.preventDefault();
    openOrFetchModal(btn.dataset.productHandle, btn);
  });

  // ────────────────────────────────────────────────────────────────────────────
  // LAZY MODAL FETCH + BUILD
  // ────────────────────────────────────────────────────────────────────────────

  function openOrFetchModal(handle, triggerBtn) {
    if (modalCache[handle]) {
      openModal(modalCache[handle], triggerBtn);
      return;
    }

    triggerBtn.disabled = true;
    triggerBtn.textContent = 'Loading…';

    fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('product fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (product) {
        ensureModalsContainer();
        var modal = buildModal(product);
        modalsContainer.appendChild(modal);
        modalCache[handle] = modal;
        triggerBtn.disabled = false;
        triggerBtn.textContent = 'Add to Bundle';
        openModal(modal, triggerBtn);
      })
      .catch(function (err) {
        console.warn('[ss-bundle-builder] product fetch:', err);
        triggerBtn.disabled = false;
        triggerBtn.textContent = 'Add to Bundle';
      });
  }

  function ensureModalsContainer() {
    if (modalsContainer) return;
    modalsContainer = document.createElement('div');
    modalsContainer.id = 'ss-bb-modals';
    document.body.appendChild(modalsContainer);
    modalsContainer.addEventListener('click', handleModalClick);
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MODAL BUILDER
  // ────────────────────────────────────────────────────────────────────────────

  function buildModal(product) {
    var firstVariant = product.variants.find(function (v) { return v.available; }) || product.variants[0];

    var variantsData = product.variants.map(function (v) {
      return {
        id: v.id,
        option1: v.option1 || '',
        option2: v.option2 || '',
        option3: v.option3 || '',
        available: v.available,
        price: v.price,
        compare_at_price: v.compare_at_price || 0,
        featured_image: v.featured_image ? (v.featured_image.src || '') : ''
      };
    });

    var hasDefaultVariant = product.variants.length === 1 && product.variants[0].title === 'Default Title';
    // /products/<handle>.js returns images as URL strings, not image objects
    var featuredImg = product.featured_image || (product.images && product.images[0]) || '';

    var optionsHtml = '';
    if (!hasDefaultVariant && product.options && product.options.length) {
      optionsHtml = '<div class="ss-bundle-builder__modal-options">';
      product.options.forEach(function (optName, optIdx) {
        var optLabel = (optName && typeof optName === 'object') ? (optName.name || '') : optName;
        var vals = getOptionValues(product.variants, optIdx);
        optionsHtml += '<div class="ss-bundle-builder__modal-option" data-ss-modal-option-index="' + optIdx + '">';
        optionsHtml += '<div class="ss-bundle-builder__modal-option-label">' + esc(optLabel) + '</div>';
        optionsHtml += '<div class="ss-bundle-builder__modal-option-values">';
        vals.forEach(function (value) {
          var key = 'option' + (optIdx + 1);
          var isSelected = firstVariant[key] === value;
          var isAvail = product.variants.some(function (v) { return v[key] === value && v.available; });
          optionsHtml += '<button type="button"'
            + ' class="ss-bundle-builder__modal-option-value' + (isSelected ? ' ss-bundle-builder__modal-option-value--selected' : '') + '"'
            + ' data-ss-modal-option-value'
            + ' data-option-index="' + optIdx + '"'
            + ' data-option-value="' + escAttr(value) + '"'
            + ' aria-pressed="' + (isSelected ? 'true' : 'false') + '"'
            + (isAvail ? '' : ' disabled')
            + '>' + esc(value) + '</button>';
        });
        optionsHtml += '</div></div>';
      });
      optionsHtml += '</div>';
    }

    var compareAtHtml = (firstVariant.compare_at_price && firstVariant.compare_at_price > firstVariant.price)
      ? '<span class="ss-bundle-builder__modal-price-compare" data-ss-modal-price-compare>' + formatMoney(firstVariant.compare_at_price) + '</span>'
      : '';

    var modal = document.createElement('div');
    modal.className = 'ss-bundle-builder__modal';
    modal.setAttribute('data-ss-modal', '');
    modal.setAttribute('data-product-id', String(product.id));
    modal.setAttribute('data-variants', JSON.stringify(variantsData));
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'ss-modal-title-' + product.id);
    modal.hidden = true;

    modal.innerHTML = ''
      + '<div class="ss-bundle-builder__modal-backdrop" data-ss-modal-backdrop></div>'
      + '<div class="ss-bundle-builder__modal-panel">'
      +   '<button type="button" class="ss-bundle-builder__modal-close" data-ss-modal-close aria-label="Close">'
      +     '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
      +       '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>'
      +     '</svg>'
      +   '</button>'
      +   '<div class="ss-bundle-builder__modal-body">'
      +     '<div class="ss-bundle-builder__modal-image-wrap">'
      +       (featuredImg ? '<img class="ss-bundle-builder__modal-image" data-ss-modal-image src="' + escAttr(featuredImg) + '" alt="' + escAttr(product.title) + '" loading="lazy">' : '')
      +     '</div>'
      +     '<div class="ss-bundle-builder__modal-info">'
      +       '<h2 class="ss-bundle-builder__modal-title" id="ss-modal-title-' + product.id + '">' + esc(product.title) + '</h2>'
      +       '<div class="ss-bundle-builder__modal-price" data-ss-modal-price>'
      +         compareAtHtml
      +         '<span class="ss-bundle-builder__modal-price-current" data-ss-modal-price-current>' + formatMoney(firstVariant.price) + '</span>'
      +       '</div>'
      +       optionsHtml
      +       '<div class="ss-bundle-builder__modal-qty">'
      +         '<span class="ss-bundle-builder__modal-qty-label">Quantity</span>'
      +         '<div class="ss-bundle-builder__modal-qty-stepper">'
      +           '<button type="button" class="ss-bundle-builder__modal-qty-btn" data-ss-modal-qty-decrement aria-label="Decrease quantity">−</button>'
      +           '<input type="number" class="ss-bundle-builder__modal-qty-input" data-ss-modal-qty-input min="1" value="1" inputmode="numeric" aria-label="Quantity">'
      +           '<button type="button" class="ss-bundle-builder__modal-qty-btn" data-ss-modal-qty-increment aria-label="Increase quantity">+</button>'
      +         '</div>'
      +       '</div>'
      +       '<button type="button"'
      +         ' class="ss-bundle-builder__modal-add"'
      +         ' data-ss-modal-add'
      +         ' data-current-variant-id="' + firstVariant.id + '"'
      +         (firstVariant.available ? '' : ' disabled aria-disabled="true"')
      +       '>'
      +         '<span class="ss-bundle-builder__modal-add-label" data-ss-modal-add-label>' + (firstVariant.available ? 'Add to Bundle' : 'Sold out') + '</span>'
      +       '</button>'
      +     '</div>'
      +   '</div>'
      + '</div>';

    return modal;
  }

  function getOptionValues(variants, optIdx) {
    var seen = new Set();
    var vals = [];
    variants.forEach(function (v) {
      var val = v['option' + (optIdx + 1)];
      if (val && !seen.has(val)) { seen.add(val); vals.push(val); }
    });
    return vals;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MODAL EVENT HANDLER (delegated on #ss-bb-modals)
  // ────────────────────────────────────────────────────────────────────────────

  function handleModalClick(e) {
    if (e.target.closest('[data-ss-modal-close]') || e.target.matches('[data-ss-modal-backdrop]')) {
      closeModalIfOpen();
      return;
    }

    var optBtn = e.target.closest('[data-ss-modal-option-value]');
    if (optBtn) {
      var row = optBtn.closest('.ss-bundle-builder__modal-option-values');
      if (row) {
        row.querySelectorAll('[data-ss-modal-option-value]').forEach(function (b) {
          b.classList.remove('ss-bundle-builder__modal-option-value--selected');
          b.setAttribute('aria-pressed', 'false');
        });
        optBtn.classList.add('ss-bundle-builder__modal-option-value--selected');
        optBtn.setAttribute('aria-pressed', 'true');
      }
      updateModalVariantState(optBtn.closest('[data-ss-modal]'));
      return;
    }

    var dec = e.target.closest('[data-ss-modal-qty-decrement]');
    var inc = e.target.closest('[data-ss-modal-qty-increment]');
    if (dec || inc) {
      var modal = (dec || inc).closest('[data-ss-modal]');
      var input = modal.querySelector('[data-ss-modal-qty-input]');
      if (input) {
        var val = parseInt(input.value, 10) || 1;
        if (dec) val = Math.max(1, val - 1);
        if (inc) val++;
        input.value = val;
      }
      return;
    }

    var addBtn = e.target.closest('[data-ss-modal-add]');
    if (!addBtn || addBtn.disabled) return;

    var addModal = addBtn.closest('[data-ss-modal]');
    var variantId = addBtn.dataset.currentVariantId;
    var qtyInput = addModal.querySelector('[data-ss-modal-qty-input]');
    var qty = parseInt(qtyInput ? qtyInput.value : '1', 10) || 1;
    if (!variantId) return;

    addBtn.disabled = true;
    var label = addModal.querySelector('[data-ss-modal-add-label]');
    var prevLabel = label ? label.textContent : '';
    if (label) label.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add.js failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderSummary(cart);
        closeModalIfOpen();
        if (window.SsCartDrawer && typeof window.SsCartDrawer.refresh === 'function') {
          window.SsCartDrawer.refresh();
        }
        addBtn.disabled = false;
      })
      .catch(function (err) {
        console.warn('[ss-bundle-builder] add to cart failed:', err);
        if (label) label.textContent = prevLabel || 'Add to Bundle';
        addBtn.disabled = false;
      });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MODAL OPEN / CLOSE
  // ────────────────────────────────────────────────────────────────────────────

  function openModal(modal, trigger) {
    closeModalIfOpen();
    lastFocus = trigger || document.activeElement;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    openModalEl = modal;
    var qtyInput = modal.querySelector('[data-ss-modal-qty-input]');
    if (qtyInput) qtyInput.value = 1;
    updateModalVariantState(modal);
    var closeBtn = modal.querySelector('[data-ss-modal-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModalIfOpen() {
    if (!openModalEl) return;
    openModalEl.hidden = true;
    document.body.style.overflow = '';
    openModalEl = null;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openModalEl) closeModalIfOpen();
  });

  // ────────────────────────────────────────────────────────────────────────────
  // VARIANT STATE
  // ────────────────────────────────────────────────────────────────────────────

  function updateModalVariantState(modal) {
    if (!modal) return;
    var variants;
    try { variants = JSON.parse(modal.dataset.variants || '[]'); } catch (e) { variants = []; }

    var selected = [];
    modal.querySelectorAll('[data-ss-modal-option-value].ss-bundle-builder__modal-option-value--selected').forEach(function (b) {
      var idx = parseInt(b.dataset.optionIndex, 10);
      if (!isNaN(idx)) selected[idx] = b.dataset.optionValue;
    });

    var matched = variants.find(function (v) {
      if (selected[0] !== undefined && v.option1 !== selected[0]) return false;
      if (selected[1] !== undefined && v.option2 !== selected[1]) return false;
      if (selected[2] !== undefined && v.option3 !== selected[2]) return false;
      return true;
    });

    var addBtn   = modal.querySelector('[data-ss-modal-add]');
    var addLabel = modal.querySelector('[data-ss-modal-add-label]');
    var priceCur = modal.querySelector('[data-ss-modal-price-current]');
    var priceCmp = modal.querySelector('[data-ss-modal-price-compare]');

    if (matched) {
      addBtn.dataset.currentVariantId = matched.id;
      if (matched.available) {
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-disabled');
        if (addLabel) addLabel.textContent = 'Add to Bundle';
      } else {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-disabled', 'true');
        if (addLabel) addLabel.textContent = 'Sold out';
      }

      // Update variant image if one exists
      var modalImage = modal.querySelector('[data-ss-modal-image]');
      if (modalImage && matched.featured_image) {
        modalImage.src = matched.featured_image;
      }

      if (priceCur) priceCur.textContent = formatMoney(matched.price);
      if (priceCmp) {
        if (matched.compare_at_price && matched.compare_at_price > matched.price) {
          priceCmp.textContent = formatMoney(matched.compare_at_price);
          priceCmp.style.display = '';
        } else {
          priceCmp.style.display = 'none';
        }
      }
    } else if (addBtn) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
      if (addLabel) addLabel.textContent = 'Unavailable';
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // SUMMARY PANEL
  // ────────────────────────────────────────────────────────────────────────────

  function renderSummary(cart) {
    if (!cart || !cart.items) return;
    var items = cart.items.filter(function (i) {
      return eligibleProductIds.has(String(i.product_id))
        && !(i.product_type && i.product_type.toLowerCase().includes('rewards-service'))
        && !((i.product_tags || '') && i.product_tags.includes('rewards-service'));
    });
    var totalQty = items.reduce(function (s, i) { return s + i.quantity; }, 0);

    if (totalQty === 0) {
      summaryEl.hidden = true;
      document.body.style.paddingBottom = '';
      return;
    }

    summaryEl.hidden = false;
    document.body.style.paddingBottom = (summaryEl.offsetHeight || 120) + 'px';

    var upsellEl = summaryEl.querySelector('[data-ss-summary-upsell]');
    if (upsellEl) upsellEl.textContent = computeUpsellMessage(totalQty);

    var itemsContainer = summaryEl.querySelector('[data-ss-summary-items]');
    var tpl = summaryEl.querySelector('[data-ss-summary-chip-template]');
    if (itemsContainer && tpl) {
      itemsContainer.innerHTML = '';
      items.forEach(function (item) {
        var chip = tpl.content.firstElementChild.cloneNode(true);
        chip.dataset.lineKey = item.key;
        var img = chip.querySelector('[data-ss-chip-image]');
        if (img) { img.src = item.image || ''; img.alt = item.product_title || ''; }
        var titleEl = chip.querySelector('[data-ss-chip-title]');
        if (titleEl) titleEl.textContent = item.product_title || '';
        var variantEl = chip.querySelector('[data-ss-chip-variant]');
        if (variantEl) variantEl.textContent = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
        var priceEl = chip.querySelector('[data-ss-chip-price]');
        if (priceEl) priceEl.textContent = formatMoney(item.final_line_price || (item.final_price * item.quantity));
        var qtyEl = chip.querySelector('[data-ss-chip-qty]');
        if (qtyEl) qtyEl.textContent = item.quantity;
        itemsContainer.appendChild(chip);
      });
    }

    var subtotal = items.reduce(function (s, i) { return s + i.original_line_price; }, 0);
    var total    = items.reduce(function (s, i) { return s + i.final_line_price; }, 0);
    var discount = subtotal - total;

    var countEl = summaryEl.querySelector('[data-ss-summary-count]');
    if (countEl) countEl.textContent = totalQty + (totalQty === 1 ? ' item' : ' items') + ' in bundle';
    var subtotalEl = summaryEl.querySelector('[data-ss-summary-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);
    var discRow = summaryEl.querySelector('[data-ss-summary-discount-row]');
    var discEl  = summaryEl.querySelector('[data-ss-summary-discount]');
    if (discount > 0) {
      if (discRow) discRow.hidden = false;
      if (discEl) discEl.textContent = '−' + formatMoney(discount);
    } else {
      if (discRow) discRow.hidden = true;
    }
    var totalEl = summaryEl.querySelector('[data-ss-summary-total]');
    if (totalEl) totalEl.textContent = formatMoney(total);
  }

  function computeUpsellMessage(qty) {
    var t1 = settings.tier1, t2 = settings.tier2, t3 = settings.tier3;
    if (qty >= t3.qty) return settings.upsellComplete;
    if (qty >= t2.qty) return tmpl(settings.upsellTemplate, t3.qty - qty, t3.pct);
    if (qty >= t1.qty) return tmpl(settings.upsellTemplate, t2.qty - qty, t2.pct);
    return tmpl(settings.upsellTemplate, t1.qty - qty, t1.pct);
  }

  function tmpl(template, n, pct) {
    return template.replace(/\[n\]/g, String(n)).replace(/\[pct\]/g, String(pct));
  }

  // Chip controls + Review Bundle button (delegated on summary element)
  summaryEl.addEventListener('click', function (e) {
    if (e.target.closest('[data-ss-summary-review]')) {
      if (window.SsCartDrawer && typeof window.SsCartDrawer.open === 'function') {
        window.SsCartDrawer.open();
      } else {
        var cartIcon = document.querySelector('.js-drawer-open-cart, [data-ss-cart-open]');
        if (cartIcon) cartIcon.click();
      }
      return;
    }

    var chip = e.target.closest('[data-ss-summary-chip]');
    if (!chip) return;
    var lineKey = chip.dataset.lineKey;
    if (!lineKey) return;

    var qtySpan = chip.querySelector('[data-ss-chip-qty]');
    var currentQty = parseInt(qtySpan ? qtySpan.textContent : '1', 10) || 1;
    var nextQty = null;
    if (e.target.closest('[data-ss-chip-decrement]')) nextQty = Math.max(0, currentQty - 1);
    else if (e.target.closest('[data-ss-chip-increment]')) nextQty = currentQty + 1;
    else if (e.target.closest('[data-ss-chip-remove]')) nextQty = 0;

    if (nextQty === null) return;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: nextQty })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderSummary(cart);
        if (window.SsCartDrawer && typeof window.SsCartDrawer.refresh === 'function') {
          window.SsCartDrawer.refresh();
        }
      })
      .catch(function (err) { console.warn('[ss-bundle-builder] cart change failed:', err); });
  });

  // ────────────────────────────────────────────────────────────────────────────
  // MONEY FORMATTER
  // ────────────────────────────────────────────────────────────────────────────

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents); } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // ────────────────────────────────────────────────────────────────────────────
  // STRING ESCAPING (for JS-built modal HTML)
  // ────────────────────────────────────────────────────────────────────────────

  function esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escAttr(s) {
    return String(s || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MUTATIONOBSERVER — re-inject buttons when Impulse updates the grid
  // ────────────────────────────────────────────────────────────────────────────

  function setupObserver() {
    var ajaxContent = document.getElementById('CollectionAjaxContent');
    if (!ajaxContent) return;

    var debounceTimer;
    new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        collectProductIds();
        injectButtons();
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(renderSummary)
          .catch(function () {});
      }, 120);
    }).observe(ajaxContent, { childList: true, subtree: true });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // INIT
  // ────────────────────────────────────────────────────────────────────────────

  collectProductIds();
  injectButtons();
  fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(renderSummary)
    .catch(function (err) { console.warn('[ss-bundle-builder] initial cart fetch failed:', err); });
  setupObserver();

})();
